import api from './client'

export type TrackingMode = 'DEDICATED' | 'EARMARK'
export type GoalStatus = 'ACTIVE' | 'PAUSED' | 'COMPLETED'
export type PaceStatus = 'REACHED' | 'NO_DATE' | 'OVERDUE' | 'NEW' | 'ON_TRACK' | 'BEHIND'

export interface GoalResponse {
  id: string
  name: string
  targetAmount: number
  currentAmount: number
  targetDate?: string
  trackingMode: TrackingMode
  status: GoalStatus
  linkedAccountId: string
  linkedAccountName: string
  attributedToHouseholdMemberId?: string
  attributedMemberName?: string
  notes?: string
  createdAt: string
  progressPercent: number
  hasContributions: boolean
  earmarkShortfall: number
  // Optional: the web app can be live before the API that adds them. Pace fields are ACTIVE goals only.
  icon?: string | null
  addedThisMonth?: number | null
  monthlyPace?: number | null
  neededPerMonth?: number | null
  paceStatus?: PaceStatus | null
  projectedDate?: string | null
}

export interface EarmarkAccount {
  accountId: string
  accountName: string
  balance: number
  earmarked: number
  free: number
}

export interface GoalListResponse {
  active: GoalResponse[]
  paused: GoalResponse[]
  completed: GoalResponse[]
  earmarkAccounts?: EarmarkAccount[]
}

export interface GoalHistoryPoint {
  month: string
  added: number
  total: number
}

export interface GoalContributionResponse {
  id: string
  amount: number
  sourceAccountId?: string
  sourceAccountName?: string
  transferId: string
  note?: string
  contributedAt: string
}

export interface EarmarkResponse {
  totalBalance: number
  earmarked: number
  available: number
  goals: Array<{ id: string; name: string; earmarkedAmount: number }>
}

export function listGoals(): Promise<GoalListResponse> {
  return api.get('/goals').then((r) => r.data)
}

export function createGoal(p: object): Promise<GoalResponse> {
  return api.post('/goals', p).then((r) => r.data)
}

export function updateGoal(id: string, p: object): Promise<GoalResponse> {
  return api.patch(`/goals/${id}`, p).then((r) => r.data)
}

export function deleteGoal(id: string): Promise<void> {
  return api.delete(`/goals/${id}`)
}

export function contributeToGoal(id: string, p: object): Promise<GoalResponse> {
  return api.post(`/goals/${id}/contribute`, p).then((r) => r.data)
}

export function withdrawFromGoal(id: string, p: object): Promise<GoalResponse> {
  return api.post(`/goals/${id}/withdraw`, p).then((r) => r.data)
}

export function pauseGoal(id: string): Promise<GoalResponse> {
  return api.post(`/goals/${id}/pause`).then((r) => r.data)
}

export function resumeGoal(id: string): Promise<GoalResponse> {
  return api.post(`/goals/${id}/resume`).then((r) => r.data)
}

export function completeGoal(id: string): Promise<GoalResponse> {
  return api.post(`/goals/${id}/complete`).then((r) => r.data)
}

export function reactivateGoal(id: string): Promise<GoalResponse> {
  return api.post(`/goals/${id}/reactivate`).then((r) => r.data)
}

export function listContributions(id: string): Promise<GoalContributionResponse[]> {
  return api.get(`/goals/${id}/contributions`).then((r) => r.data)
}

export function deleteContribution(goalId: string, contributionId: string): Promise<void> {
  return api.delete(`/goals/${goalId}/contributions/${contributionId}`)
}

export function getGoalHistory(id: string): Promise<GoalHistoryPoint[]> {
  return api.get(`/goals/${id}/history`).then((r) => r.data)
}

export function getEarmarks(accountId: string): Promise<EarmarkResponse> {
  return api.get(`/accounts/${accountId}/earmarks`).then((r) => r.data)
}
